import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { Subscription, fromEvent } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { MenuItem } from '../common/menu-item';
import { ContextualMenuChange } from './models/contextual-menu-change';
import { ContextualMenuService } from './services/contextual-menu-service';

@Component({
    selector: 'app-contextual-menu',
    templateUrl: './contextual-menu.component.html',
    styleUrls: ['./contextual-menu.component.scss'],
    imports: []
})
export class ContextualMenuComponent<T, U> {
    @Input()
    items: MenuItem<U>[] = [];

    filteredMenuItems: MenuItem<U>[] = [];

    @Input()
    direction: 'LEFT' | 'RIGHT';

    @Output()
    menuChanged: EventEmitter<ContextualMenuChange<T, U>> = new EventEmitter<ContextualMenuChange<T, U>>();

    leftPosition: ConnectedPosition = {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
    };

    rightPosition: ConnectedPosition = {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
    };

    currentPosition = this.leftPosition;

    constructor(
        private contextualMenuService: ContextualMenuService<T, U>,
        public overlay: Overlay,
        public viewContainerRef: ViewContainerRef
    ) {}

    public onSelection(action: U, row: T) {
        this.menuChanged.emit(new ContextualMenuChange<T, U>(action, row));
        this.close();
    }

    displayMenu({ x, y }, row: T) {
        this.close();

        if (this.direction === 'RIGHT') {
            this.currentPosition = this.rightPosition;
        }

        const positionStrategy = this.overlay.position().flexibleConnectedTo({ x, y }).withPositions([this.currentPosition]);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.close(),
        });

        this.overlayRef.attach(
            new TemplatePortal(this.contextMenu, this.viewContainerRef, {
                $implicit: row,
            })
        );
    }
    S;

    open(event: MouseEvent, row: T) {
        this.displayMenu(event, row);

        let tempMenuItems = [];

        this.items.forEach((item) => {
            if (item.condition) {
                if (item.condition(row)) {
                    tempMenuItems.push(item);
                }
            } else {
                tempMenuItems.push(item);
            }
        });

        this.filteredMenuItems = tempMenuItems;

        this.sub = fromEvent<MouseEvent>(document, 'click')
            .pipe(
                filter((event2) => {
                    const clickTarget = event2.target as HTMLElement;
                    return (
                        !!this.overlayRef && !this.overlayRef.overlayElement.contains(clickTarget) && (event.button === 2 || (event.button == 0 && clickTarget !== event.target))
                    );
                }),
                take(1)
            )
            .subscribe(() => this.close());
    }

    close() {
        this.sub && this.sub.unsubscribe();
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }

    @ViewChild('contextMenu') contextMenu: TemplateRef<any>;

    overlayRef: OverlayRef | null;

    sub: Subscription;
}
